import { useState, useEffect, useCallback } from 'react';
import { 
  fetchActivityData, 
  ActivityType, 
  updateActivity, 
  deleteActivity 
} from '@/lib/services/activity-service';
import { useQueryClient } from '@tanstack/react-query';
import { useChildStore } from '@/lib/store/child';

/**
 * Custom hook to provide data for ActivityTables component
 * 
 * @param childId - The ID of the child to fetch activities for
 * @param options - Options for filtering activities
 * @returns Object with activity data, loading state, and error
 */
export function useActivityTables(
  childId: string | null,
  options?: {
    timeRange?: { start: string; end: string } | null;
    searchQuery?: string;
    useGlobalChildStore?: boolean; // If true, will use the global child store as a fallback
  }
) {
  const queryClient = useQueryClient();
  const { selectedChild } = useChildStore();
  const [sleepActivities, setSleepActivities] = useState<ActivityType[]>([]);
  const [napActivities, setNapActivities] = useState<ActivityType[]>([]);
  const [feedingActivities, setFeedingActivities] = useState<ActivityType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [tabValue, setTabValue] = useState<'all' | 'sleep' | 'nap' | 'feeding'>('all');
  
  // Use either passed childId or fallback to global store if option is enabled
  const effectiveChildId = useCallback(() => {
    if (childId) return childId;
    if (options?.useGlobalChildStore && selectedChild?.id) return selectedChild.id;
    return null;
  }, [childId, options?.useGlobalChildStore, selectedChild?.id]);

  // Load activities effect
  useEffect(() => {
    const currentChildId = effectiveChildId();
    
    async function loadActivities() {
      if (!currentChildId) {
        // Reset state if no child is selected
        setSleepActivities([]);
        setNapActivities([]);
        setFeedingActivities([]);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const { timeRange, searchQuery } = options || {};
        
        console.log(`[useActivityTables] Loading activities for child ${currentChildId}`);
        
        const activities = await fetchActivityData(currentChildId, {
          fetchSleep: true,
          fetchNap: true,
          fetchFeeding: true,
          timeRange: timeRange || undefined,
          searchQuery
        });

        // Filter activities by type from the flat array
        const sleepActivities = activities.filter(activity => activity.type === 'sleep');
        const napActivities = activities.filter(activity => activity.type === 'nap');
        const feedingActivities = activities.filter(activity => activity.type === 'feeding');

        // Set states with the filtered data
        setSleepActivities(sleepActivities);
        setNapActivities(napActivities);
        setFeedingActivities(feedingActivities);
      } catch (err) {
        console.error('[useActivityTables] Error fetching activities:', err);
        setError(err instanceof Error ? err : new Error('Failed to load activities'));
      } finally {
        setIsLoading(false);
      }
    }

    loadActivities();
  }, [effectiveChildId, options?.timeRange, options?.searchQuery]);

  // Handler for tab changes
  const handleTabChange = (value: 'all' | 'sleep' | 'nap' | 'feeding') => {
    setTabValue(value);
  };
  
  // Handler for editing activities
  const handleEditActivity = async (
    activity: ActivityType, 
    type: 'sleep' | 'nap' | 'feeding',
    updates: any
  ) => {
    const currentChildId = effectiveChildId();
    if (!currentChildId || !activity.id) return null;
    
    try {
      // Make sure childId is correctly passed
      const result = await updateActivity(activity.id, type, {
        ...updates,
        childId: currentChildId // Ensure the correct childId is used
      });
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({
        queryKey: ['activities', currentChildId, type]
      });
      return result;
    } catch (error) {
      console.error(`[useActivityTables] Error updating ${type} activity:`, error);
      throw error;
    }
  };
  
  // Handler for deleting activities
  const handleDeleteActivity = async (
    activity: ActivityType, 
    type: 'sleep' | 'nap' | 'feeding'
  ) => {
    const currentChildId = effectiveChildId();
    if (!currentChildId || !activity.id) return false;
    
    try {
      await deleteActivity(activity.id, type);
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({
        queryKey: ['activities', currentChildId, type]
      });
      return true;
    } catch (error) {
      console.error(`[useActivityTables] Error deleting ${type} activity:`, error);
      throw error;
    }
  };

  return {
    sleepActivities,
    napActivities,
    feedingActivities,
    isLoading,
    error,
    tabValue,
    onTabChange: handleTabChange,
    editActivity: handleEditActivity,
    deleteActivity: handleDeleteActivity,
    currentChildId: effectiveChildId()
  };
}
